import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Button,
  Container,
  Flex,
  IconButton,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Textarea,
  Tooltip,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import { ViewIcon, CopyIcon } from '@chakra-ui/icons';
import {
  getWithdrawalRequests,
  processWithdrawalRequest,
  isSuccess,
  extractData,
} from '../../../service/WithdrawalService';

const STATUS_COLORS = {
  PENDING: '#e6a817',
  SUCCESS: '#28a745',
  REJECTED: '#dc3545',
};

const STATUS_FILTERS = [
  { label: 'All', value: '' },
  { label: 'Pending', value: 'PENDING' },
  { label: 'Approved', value: 'SUCCESS' },
  { label: 'Rejected', value: 'REJECTED' },
];

const BANK_TYPES = ['BANK_SAVING', 'CURRENT_BANK'];
const UPI_TYPES = ['UPI_PHONEPE', 'UPI_GPAY', 'UPI_PAYTM', 'UPI_BHIM'];
const CRYPTO_TYPES = ['CRYPTO'];

const getPaymentCategory = (type) => {
  if (BANK_TYPES.includes(type)) return 'BANK';
  if (UPI_TYPES.includes(type)) return 'UPI';
  if (CRYPTO_TYPES.includes(type)) return 'CRYPTO';
  return 'OTHER';
};

const formatDate = (dateStr) => {
  if (!dateStr) return '—';
  try {
    return new Date(dateStr).toLocaleString('en-IN', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return dateStr;
  }
};

const getPaymentDetail = (item) => {
  if (!item) return '—';
  if (item.bankName) {
    return `${item.bankName} - ****${(item.accountNumber || '').slice(-4)}`;
  }
  if (item.upiValue) return item.upiValue;
  if (item.usdtAddress) {
    return `${item.chain || ''} - ${(item.usdtAddress || '').slice(0, 12)}...`;
  }
  return '—';
};

const getPaymentFields = (item) => {
  if (!item) return [];
  const cat = getPaymentCategory(item.paymentType);
  if (cat === 'BANK') {
    return [
      { label: 'Bank Name', value: item.bankName },
      { label: 'Account Number', value: item.accountNumber },
      { label: 'IFSC Code', value: item.ifscCode },
      { label: 'Account Holder', value: item.accountHolderName },
    ];
  }
  if (cat === 'UPI') {
    return [
      { label: 'UPI ID', value: item.upiValue },
      { label: 'Name', value: item.accountHolderName },
      { label: 'Mobile', value: item.upiMobileNumber },
    ];
  }
  if (cat === 'CRYPTO') {
    return [
      { label: 'Chain', value: item.chain },
      { label: 'USDT Address', value: item.usdtAddress },
    ];
  }
  return [];
};

const detailRowStyle = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: '10px 12px',
  borderBottom: '1px solid #eee',
};

const detailLabelStyle = {
  fontWeight: '600',
  fontSize: '13px',
  color: '#555',
  minWidth: '120px',
};

const detailValueStyle = {
  fontSize: '14px',
  fontWeight: '500',
  flex: 1,
  textAlign: 'right',
  wordBreak: 'break-all',
  marginLeft: '12px',
};

export default function WithdrawalRequests() {
  const toast = useToast();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const {
    isOpen: isDetailOpen,
    onOpen: onDetailOpen,
    onClose: onDetailClose,
  } = useDisclosure();

  const [data, setData] = useState([]);
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage] = useState(0);
  const [size] = useState(20);
  const [processing, setProcessing] = useState(false);

  const [selectedRequest, setSelectedRequest] = useState(null);
  const [detailItem, setDetailItem] = useState(null);
  const [remark, setRemark] = useState('');

  const copyToClipboard = (text, label) => {
    if (!text) return;
    navigator.clipboard.writeText(text).then(() => {
      toast({
        title: `${label} copied`,
        status: 'info',
        duration: 1500,
        isClosable: true,
        position: 'top',
      });
    });
  };

  const fetchData = useCallback(async () => {
    const res = await getWithdrawalRequests(statusFilter || undefined);
    const rawData = extractData(res);

    if (isSuccess(res) && Array.isArray(rawData)) {
      setData(rawData);
    } else if (Array.isArray(res)) {
      setData(res);
    } else if (Array.isArray(rawData)) {
      setData(rawData);
    } else {
      setData([]);
    }
  }, [statusFilter]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    setPage(0);
  }, [statusFilter]);

  const paginatedData = data.slice(page * size, (page + 1) * size);

  const nextPage = () => {
    if ((page + 1) * size < data.length) setPage(page + 1);
  };
  const prevPage = () => {
    if (page > 0) setPage(page - 1);
  };

  const openProcessModal = (request) => {
    setSelectedRequest(request);
    setRemark('');
    onOpen();
  };

  const openDetailModal = (item) => {
    setDetailItem(item);
    onDetailOpen();
  };

  const handleProcess = async (approved) => {
    if (!selectedRequest) return;
    setProcessing(true);
    const res = await processWithdrawalRequest({
      withdrawalRequestId: selectedRequest.id,
      approved,
      remark,
    });
    setProcessing(false);

    if (isSuccess(res)) {
      toast({
        title: approved ? 'Request Approved' : 'Request Rejected',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      onClose();
      fetchData();
    } else {
      toast({
        title: res?.message || 'Error processing request',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const buttonStyles = (value) => ({
    background:
      value === statusFilter
        ? 'linear-gradient(to bottom, #f0c454, #d4a017)'
        : 'transparent',
    color: value === statusFilter ? '#1a1a2e' : 'black',
    fontWeight: value === statusFilter ? '600' : '400',
    border: value === statusFilter ? 'none' : '1px solid black',
    borderRadius: '10px',
    padding: '5px 10px',
    cursor: 'pointer',
    outline: 'none',
  });

  const goldButtonStyle = {
    background: 'linear-gradient(to bottom, #f0c454, #d4a017)',
    color: '#1a1a2e',
    fontWeight: '600',
    border: 'none',
  };

  return (
    <div className='main'>
      <div className='container-fluid'>
        <div className='row'>
          <div className='col-md-12 main-container'>
            <div>
              <div className='listing-grid'>
                <div className='detail-row'>
                  <h2>Withdrawal Requests</h2>
                </div>

                <Flex gap='5px' mt='10px'>
                  {STATUS_FILTERS.map((f) => (
                    <button
                      key={f.value}
                      onClick={() => setStatusFilter(f.value)}
                      style={buttonStyles(f.value)}
                    >
                      {f.label}
                    </button>
                  ))}
                </Flex>

                <div
                  style={{ marginTop: '25px' }}
                  className='table-responsive data-table'
                >
                  <div className='dataTables_wrapper no-footer'>
                    <table className='table table-bordered datatable dataTable no-footer'>
                      <thead>
                        <tr role='row' style={{ backgroundColor: '#C9C9C9' }}>
                          <th>ID</th>
                          <th>Txn ID</th>
                          <th>Username</th>
                          <th>Amount</th>
                          <th>Payment Type</th>
                          <th>Payment Details</th>
                          <th>Status</th>
                          <th>Date</th>
                          <th>Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {paginatedData.length === 0 && (
                          <tr style={{ backgroundColor: '#FFFFFF' }}>
                            <td
                              colSpan={9}
                              className='dataTables_empty'
                              style={{ textAlign: 'center' }}
                            >
                              No withdrawal requests found
                            </td>
                          </tr>
                        )}
                        {paginatedData.map((item) => (
                          <tr key={item.id} role='row' className='odd'>
                            <td>{item.id}</td>
                            <td style={{ fontSize: '12px' }}>{item.transactionId || '—'}</td>
                            <td>{item.username || item.userName || '—'}</td>
                            <td>{item.amount}</td>
                            <td>{item.paymentType || '—'}</td>
                            <td>
                              <Flex align='center' gap='6px'>
                                <span>{getPaymentDetail(item)}</span>
                                <Tooltip label='View full details' fontSize='xs'>
                                  <IconButton
                                    aria-label='View payment details'
                                    icon={<ViewIcon />}
                                    size='xs'
                                    variant='ghost'
                                    colorScheme='blue'
                                    onClick={() => openDetailModal(item)}
                                  />
                                </Tooltip>
                              </Flex>
                            </td>
                            <td
                              style={{
                                color: STATUS_COLORS[item.status] || 'black',
                                fontWeight: '600',
                              }}
                            >
                              {item.status}
                            </td>
                            <td>{formatDate(item.createdAt)}</td>
                            <td>
                              {item.status === 'PENDING' ? (
                                <button
                                  style={{
                                    ...goldButtonStyle,
                                    paddingLeft: '15px',
                                    paddingRight: '15px',
                                    borderRadius: '5px',
                                  }}
                                  onClick={() => openProcessModal(item)}
                                >
                                  Process
                                </button>
                              ) : (
                                <span
                                  style={{
                                    fontSize: '12px',
                                    color: '#666',
                                  }}
                                >
                                  {item.adminRemark || '—'}
                                </span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                <div
                  className='dataTables_paginate paging_simple_numbers'
                  id='clientListTable_paginate'
                >
                  <a
                    className='previous button btn btn-diamond'
                    style={goldButtonStyle}
                    onClick={prevPage}
                    role='button'
                    tabIndex={0}
                  >
                    Previous
                  </a>
                  <a
                    className='next button btn btn-diamond'
                    style={goldButtonStyle}
                    onClick={nextPage}
                    role='button'
                    tabIndex={0}
                  >
                    Next
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Payment Details Modal */}
      <Modal isOpen={isDetailOpen} onClose={onDetailClose} isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader
            fontSize='16px'
            borderBottom='1px solid #eee'
            pb={3}
          >
            Payment Details
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody py={4} px={2}>
            {detailItem && (
              <>
                <div style={detailRowStyle}>
                  <span style={detailLabelStyle}>Username</span>
                  <Flex align='center' gap='6px' style={detailValueStyle}>
                    <span>{detailItem.username || detailItem.userName || '—'}</span>
                  </Flex>
                </div>
                <div style={detailRowStyle}>
                  <span style={detailLabelStyle}>Amount</span>
                  <Flex align='center' gap='6px' style={detailValueStyle}>
                    <span>{detailItem.amount}</span>
                  </Flex>
                </div>
                <div style={detailRowStyle}>
                  <span style={detailLabelStyle}>Payment Type</span>
                  <Flex align='center' gap='6px' style={detailValueStyle}>
                    <span>{detailItem.paymentType || '—'}</span>
                  </Flex>
                </div>

                {getPaymentFields(detailItem).map((field) => (
                  <div key={field.label} style={detailRowStyle}>
                    <span style={detailLabelStyle}>{field.label}</span>
                    <Flex align='center' gap='6px' style={detailValueStyle}>
                      <span>{field.value || '—'}</span>
                      {field.value && (
                        <Tooltip label={`Copy ${field.label}`} fontSize='xs'>
                          <IconButton
                            aria-label={`Copy ${field.label}`}
                            icon={<CopyIcon />}
                            size='xs'
                            variant='ghost'
                            onClick={() =>
                              copyToClipboard(field.value, field.label)
                            }
                          />
                        </Tooltip>
                      )}
                    </Flex>
                  </div>
                ))}

                {detailItem.mobileNumber && (
                  <div style={detailRowStyle}>
                    <span style={detailLabelStyle}>Mobile Number</span>
                    <Flex align='center' gap='6px' style={detailValueStyle}>
                      <span>{detailItem.mobileNumber}</span>
                      <Tooltip label='Copy Mobile Number' fontSize='xs'>
                        <IconButton
                          aria-label='Copy Mobile Number'
                          icon={<CopyIcon />}
                          size='xs'
                          variant='ghost'
                          onClick={() =>
                            copyToClipboard(detailItem.mobileNumber, 'Mobile Number')
                          }
                        />
                      </Tooltip>
                    </Flex>
                  </div>
                )}

                <div style={{ ...detailRowStyle, borderBottom: 'none' }}>
                  <span style={detailLabelStyle}>Status</span>
                  <span
                    style={{
                      ...detailValueStyle,
                      color: STATUS_COLORS[detailItem.status] || 'black',
                      fontWeight: '600',
                    }}
                  >
                    {detailItem.status}
                  </span>
                </div>
              </>
            )}
          </ModalBody>
        </ModalContent>
      </Modal>

      {/* Process (Approve/Reject) Modal */}
      <Modal isOpen={isOpen} onClose={onClose} size='lg'>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Process Withdrawal Request</ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            {selectedRequest && (
              <>
                <table style={{ width: '100%', marginBottom: '16px' }}>
                  <tbody>
                    <tr>
                      <td style={{ fontWeight: 'bold', padding: '4px 8px' }}>
                        Request ID:
                      </td>
                      <td style={{ padding: '4px 8px' }}>
                        {selectedRequest.id}
                      </td>
                    </tr>
                    <tr>
                      <td style={{ fontWeight: 'bold', padding: '4px 8px' }}>
                        Username:
                      </td>
                      <td style={{ padding: '4px 8px' }}>
                        {selectedRequest.username ||
                          selectedRequest.userName ||
                          '—'}
                      </td>
                    </tr>
                    <tr>
                      <td style={{ fontWeight: 'bold', padding: '4px 8px' }}>
                        Amount:
                      </td>
                      <td style={{ padding: '4px 8px' }}>
                        {selectedRequest.amount}
                      </td>
                    </tr>
                    <tr>
                      <td style={{ fontWeight: 'bold', padding: '4px 8px' }}>
                        Payment Type:
                      </td>
                      <td style={{ padding: '4px 8px' }}>
                        {selectedRequest.paymentType || '—'}
                      </td>
                    </tr>
                    {getPaymentFields(selectedRequest).map((field) => (
                      <tr key={field.label}>
                        <td style={{ fontWeight: 'bold', padding: '4px 8px' }}>
                          {field.label}:
                        </td>
                        <td style={{ padding: '4px 8px' }}>
                          <Flex align='center' gap='6px'>
                            <span>{field.value || '—'}</span>
                            {field.value && (
                              <Tooltip label={`Copy ${field.label}`} fontSize='xs'>
                                <IconButton
                                  aria-label={`Copy ${field.label}`}
                                  icon={<CopyIcon />}
                                  size='xs'
                                  variant='ghost'
                                  onClick={() =>
                                    copyToClipboard(field.value, field.label)
                                  }
                                />
                              </Tooltip>
                            )}
                          </Flex>
                        </td>
                      </tr>
                    ))}
                    <tr>
                      <td style={{ fontWeight: 'bold', padding: '4px 8px' }}>
                        Date:
                      </td>
                      <td style={{ padding: '4px 8px' }}>
                        {formatDate(selectedRequest.createdAt)}
                      </td>
                    </tr>
                  </tbody>
                </table>

                <Box mb={4}>
                  <label
                    htmlFor='remark'
                    style={{ fontWeight: 'bold', marginBottom: '4px', display: 'block' }}
                  >
                    Remark
                  </label>
                  <Textarea
                    id='remark'
                    placeholder='Enter remark (optional)'
                    value={remark}
                    onChange={(e) => setRemark(e.target.value)}
                    resize='vertical'
                  />
                </Box>

                <Container>
                  <Flex
                    gap='10px'
                    className='justify-content-center'
                    mb={3}
                  >
                    <Box>
                      <Button
                        colorScheme='green'
                        onClick={() => handleProcess(true)}
                        isLoading={processing}
                      >
                        Approve
                      </Button>
                    </Box>
                    <Box>
                      <Button
                        colorScheme='red'
                        onClick={() => handleProcess(false)}
                        isLoading={processing}
                      >
                        Reject
                      </Button>
                    </Box>
                  </Flex>
                </Container>
              </>
            )}
          </ModalBody>
        </ModalContent>
      </Modal>
    </div>
  );
}
